import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  DistributionKindEnum,
  ExpeditionBulkDistributionJobDetailOutDTO,
  ExpeditionBulkDistributionJobItemOutDTO,
  ExpeditionBulkDistributionJobItemsFilter,
  ItemStatusEnum,
  JobStatusEnum,
  JobTypeEnum,
  Pageable,
  PermissionTypesEnum,
  PhaseEnum,
  StateFilterEnum,
} from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { Link, useSearchParams } from 'react-router-dom';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import COBanner from '@components/FigmaComponents/COBanner.tsx';
import { COBannerVariant } from '@components/FigmaComponents/COBannerVariant.ts';
import COText from '@app/pages/COText/COText.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import JobDetailHeader, { JobExpiryNote } from '@components/ringil3/Features/Job/JobDetailHeader.tsx';
import ExpeditionStateSpan from '@components/obsolete/StateSpan/ExpeditionStateSpan.tsx';
import DuplicationStepper from '@components/ringil3/Features/Job/MultiduplicationV3/DuplicationStepper.tsx';
import { UiMode } from '@components/ringil3/Features/Job/MultiduplicationV3/multiduplicationV3Table.ts';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { JobReferrer } from '@components/ringil3/Features/Job/jobReferrer.ts';
import { clickableTextClass } from '@components/RingilComponents/clickableText.ts';
import SvgOK from '@icons/check-circle.svg?react';
import SvgAlert from '@icons/alert-circle.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgReset from '@icons/reset-filled.svg?react';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import { dayjsFromApiUtcInTz, formatDateFromDayjsWithLocale, stringHHmmFromDayjs } from '@utils/dateWithTz';
import { isNotNullOrUndefined } from '@utils/utils';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { useJobChannel } from '@hooks/useJobs/useJobChannel.ts';
import { formatBEValidationMessage, formatJobItemError } from '@hooks/useBEValidation/formatBEValidationMessage';

interface ComponentProps {
  jobId: string;
}

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];
const RUNNING: JobStatusEnum[] = [JobStatusEnum.PENDING, JobStatusEnum.RUNNING];
// Rows are validated / tendered faster than a debounce window, so a debounced reload would starve until
// the whole burst ends. Throttle instead: reload at most this often while hints keep coming, trailing one included.
const TABLE_RELOAD_MIN_INTERVAL_MS = 1000;
const LADEN_FACTOR_PROBLEM_PREFIX = 'exception.expedition.bulk_distribution.laden_factor';

/**
 * Job-type-specific detail for EXPEDITION_BULK_DISTRIBUTION ("Poptat dopravce" mass action run as a job).
 * Two phases, mirrored from multiduplication V3: VALIDATE checks every row and parks the job in DRAFT,
 * where the user removes problem rows (or fixes the expedition and re-checks) and then executes;
 * EXECUTE creates the distributions. One GET for the job-level card; the item table is a classic
 * RingilDataTable paged/filtered on the BE. Live source is the per-job Centrifugo channel; while running
 * the page also polls (15 s when push is down, 60 s as insurance) and refetches once when the job finishes.
 */
const ExpeditionBulkDistributionJobDetail: React.FC<ComponentProps> = ({ jobId }) => {
  const {
    getExpeditionBulkDistributionJobDetail,
    getExpeditionBulkDistributionJobItems,
    executeExpeditionBulkDistributionJob,
    revalidateExpeditionBulkDistributionJob,
    deleteExpeditionBulkDistributionJob,
    deleteExpeditionBulkDistributionItem,
    cancelAsyncJob,
    retryJob,
    archiveJob,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const nav = useNavigation();
  // Leaving a draft that still exists should land the user where they came in from; anything other
  // than an explicit EXPEDITIONS marker (direct link, bookmark, reload) falls back to the jobs centre.
  const [searchParams] = useSearchParams();
  const cameFromExpeditions = searchParams.get('jobReferrer') === JobReferrer.EXPEDITIONS;
  const leaveDraftUrl = () => (cameFromExpeditions ? nav.urlFunctions.createCustomerExpeditionList() : nav.urlFunctions.createJobList());
  const [detail, setDetail] = useState<ExpeditionBulkDistributionJobDetailOutDTO>();
  // Key of the job action in flight (RD-2954): the clicked button spins, the others grey out, so a
  // double click or a parallel action never sends a second request.
  const [action, setAction] = useState<string | null>(null);
  const busy = (key: string) => ({ loading: action === key, disabled: action != null && action !== key });
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const tableRef = useRef<{
    reloadData: () => void;
    selectedObjects: ExpeditionBulkDistributionJobItemOutDTO[];
    clear: () => void;
    applyFilterValue: (field: string, value: unknown) => void;
  }>(null);
  const reloadTimer = useRef<number | null>(null);
  const lastTableReload = useRef(0);

  const editFlag = useSecuredFlag('bulk-distribution-edit', {
    kind: 'action',
    description: 'Execute / edit bulk tendering drafts (same permission as creating the job)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
  });
  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Retry / archive jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const load = (onLoaded?: () => void) => {
    getExpeditionBulkDistributionJobDetail(jobId, d => {
      if (abortControllerRef.current?.signal.aborted) return;
      setDetail(d);
      onLoaded?.();
    });
  };

  const throttledTableReload = () => {
    if (reloadTimer.current != null) return;
    const wait = Math.max(0, lastTableReload.current + TABLE_RELOAD_MIN_INTERVAL_MS - Date.now());
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      lastTableReload.current = Date.now();
      tableRef.current?.reloadData();
    }, wait);
  };

  const reloadAll = (onLoaded?: () => void) => {
    load(onLoaded);
    throttledTableReload();
  };

  useEffect(() => {
    const ac = new AbortController();
    abortControllerRef.current = ac;
    load();
    return () => {
      ac.abort();
      if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
    };
  }, [jobId]);

  const { live } = useJobChannel(jobId, {
    onProgress: e => {
      setDetail(prev => {
        if (prev == null) return prev;
        return {
          ...prev,
          job: {
            ...prev.job,
            jobStatus: (e.status as JobStatusEnum) ?? prev.job.jobStatus,
            processedCount: e.processed ?? prev.job.processedCount,
            successCount: e.success ?? prev.job.successCount,
            failedCount: e.failed ?? prev.job.failedCount,
            totalCount: e.total ?? prev.job.totalCount,
          },
        };
      });
      throttledTableReload();
    },
    onFinished: () => reloadAll(),
    onDraftChanged: e => {
      if (e.reason === 'JOB_DELETED') {
        showToastMessage(tr('JobDetail.bulkDistribution.deleted', 'Seznam byl smazán'), '', 'info');
        nav.navigate(leaveDraftUrl());
        return;
      }
      reloadAll();
    },
  });

  const status = detail?.job?.jobStatus;
  const mode: UiMode =
    status == null || TERMINAL.includes(status)
      ? 'TERMINAL'
      : status === JobStatusEnum.DRAFT
      ? 'DRAFT'
      : detail?.phase === PhaseEnum.EXECUTE
      ? 'EXECUTING'
      : 'VALIDATING';
  const isRunning = status != null && RUNNING.includes(status);
  const isTerminal = status != null && TERMINAL.includes(status);
  const isDraftPhase = mode === 'DRAFT' || mode === 'VALIDATING';

  // The issues filter is pre-applied once per settle into DRAFT (re-armed by a VALIDATING -> DRAFT
  // transition), so the user can clear it without it snapping back.
  const appliedIssueFilter = useRef(false);
  const prevModeRef = useRef<UiMode | null>(null);
  useEffect(() => {
    if (detail == null) return;
    const prevMode = prevModeRef.current;
    prevModeRef.current = mode;
    if (mode !== 'DRAFT') return;
    if (prevMode === 'VALIDATING') appliedIssueFilter.current = false;
    if (appliedIssueFilter.current) return;
    appliedIssueFilter.current = true;
    if (detail.itemsWithIssues > 0) {
      tableRef.current?.applyFilterValue('stateFilter', StateFilterEnum.WITH_ERROR);
    }
  }, [detail == null, mode, detail?.itemsWithIssues]);

  // Poll fallback: running -> 15 s (push down) / 60 s (insurance); DRAFT + push down -> 60 s; terminal -> nothing.
  useEffect(() => {
    if (detail == null) return;
    let intervalMs: number | null = null;
    if (isRunning) intervalMs = live ? 60000 : 15000;
    else if (status === JobStatusEnum.DRAFT && !live) intervalMs = 60000;
    if (intervalMs == null) return;
    const id = setInterval(reloadAll, intervalMs);
    return () => clearInterval(id);
  }, [jobId, isRunning, status, live, detail == null]);

  const hasIssues = (row: ExpeditionBulkDistributionJobItemOutDTO) => row.itemStatus === ItemStatusEnum.ERROR || (row.problems?.length ?? 0) > 0;

  // Row deletes release their busy key only once fresh rows are in — until then the old row is still on screen.
  const rowsLoaded = () => setAction(prev => (prev != null && prev.startsWith('delete-') ? null : prev));

  const loadDataApi = (
    filter: ExpeditionBulkDistributionJobItemsFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<ExpeditionBulkDistributionJobItemOutDTO>) => void,
  ) => {
    getExpeditionBulkDistributionJobItems(jobId, filter, pageable, data => {
      rowsLoaded();
      onSuccess(data);
    });
  };

  const stateLabel = (state: StateFilterEnum) =>
    ({
      [StateFilterEnum.WITH_ERROR]: tr('JobDetail.bulkDistribution.state.WITH_ERROR', 'Jen problémy a chyby'),
      [StateFilterEnum.OK]: tr('JobDetail.bulkDistribution.state.OK', 'V pořádku'),
    }[state]);

  const kindLabel = (kind: DistributionKindEnum) =>
    ({
      [DistributionKindEnum.SPOT]: tr('JobDetail.bulkDistribution.kind.SPOT', 'Spot'),
      [DistributionKindEnum.FIXED]: tr('JobDetail.bulkDistribution.kind.FIXED', 'Fixní cena'),
      [DistributionKindEnum.ASSIGN]: tr('JobDetail.bulkDistribution.kind.ASSIGN', 'Přímé přiřazení'),
    }[kind]);

  const statusIcon = (row: ExpeditionBulkDistributionJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin shrink-0'} />;
    if (row.itemStatus === ItemStatusEnum.ERROR) return <SvgAlert className={'text-complementary-error shrink-0'} />;
    if (row.itemStatus !== ItemStatusEnum.DONE) return null;
    if (isDraftPhase && hasIssues(row)) return <SvgWarning className={'text-complementary-warning shrink-0'} />;
    return <SvgOK className={'text-complementary-success shrink-0'} />;
  };

  const renderExpeditionColumn = (row: ExpeditionBulkDistributionJobItemOutDTO) => (
    <div className={'flex flex-row gap-2 items-start'}>
      {statusIcon(row)}
      <div className={'flex flex-col gap-1 items-start'}>
        <COBadge variant={'outlined'} type={'neutral'}>
          <span className={'text-content-secondary'}>{tr('MultiduplicationJobTable.rowId', 'ID:')}</span>
          &nbsp;{row.sortOrder + 1}
        </COBadge>
        <Link className={'text-content-brand font-medium'} to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(row.expeditionId))}>
          {row.expeditionApplicationId ?? row.expeditionId}
        </Link>
        {row.expeditionStatus != null && <ExpeditionStateSpan expeditionStage={row.expeditionStatus} isManual={false} />}
      </div>
    </div>
  );

  const renderRouteColumn = (row: ExpeditionBulkDistributionJobItemOutDTO) => {
    const stops = row.routeSummary ?? [];
    if (stops.length === 0) return <div />;
    const intermediate = Math.max(stops.length - 2, 0);
    return (
      <div className={'flex flex-col gap-1'}>
        <span className={'text-content-primary'}>{stops[0]}</span>
        {intermediate > 0 && (
          <span className={'text-content-tertiary text-sm'}>{tr('MultiduplicationV3.intermediateStops', '+ {{count}} zastávek', { count: intermediate })}</span>
        )}
        {stops.length > 1 && <span className={'text-content-primary'}>{stops[stops.length - 1]}</span>}
      </div>
    );
  };

  const renderLadenFactorColumn = (row: ExpeditionBulkDistributionJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin'} />;
    if (row.itemStatus === ItemStatusEnum.PENDING) return <COText label={tr('JobDetail.bulkDistribution.waiting', 'Čeká')} />;
    if (row.itemStatus !== ItemStatusEnum.DONE || row.handlingUnitCount == null) return <span className={'text-content-tertiary'}>—</span>;
    const units = tr('JobDetail.bulkDistribution.handlingUnits', '{{count}} MJ', { count: row.handlingUnitCount });
    const ladenFactorProblem = (row.problems ?? []).some(p => p.message?.startsWith(LADEN_FACTOR_PROBLEM_PREFIX));
    return (
      <div className={'flex flex-col gap-1'}>
        {ladenFactorProblem ? (
          <span className={'flex flex-row gap-1 items-center text-complementary-warning'}>
            <SvgWarning className={'shrink-0'} />
            <COText label={units} />
          </span>
        ) : (
          <span className={'flex flex-row gap-1 items-center'}>
            <SvgOK className={'text-complementary-success shrink-0'} />
            <COText label={tr('JobDetail.bulkDistribution.allOk', 'Vše v pořádku')} />
          </span>
        )}
        {!ladenFactorProblem && <span className={'text-sm text-content-tertiary'}>{units}</span>}
      </div>
    );
  };

  const renderProblemsColumn = (row: ExpeditionBulkDistributionJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin'} />;
    if (row.itemStatus === ItemStatusEnum.PENDING) return <COText label={tr('JobDetail.bulkDistribution.waiting', 'Čeká')} />;
    if (row.itemStatus === ItemStatusEnum.ERROR) {
      return (
        <span className={'flex flex-row gap-1 items-start'}>
          <SvgAlert className={'text-complementary-error shrink-0'} />
          <span className={'text-content-tertiary text-sm'}>{formatJobItemError(row.error, row.errorMessage, tr)}</span>
        </span>
      );
    }
    const messages = (row.problems ?? []).map(p => formatBEValidationMessage(p, tr)).filter(isNotNullOrUndefined);
    if (messages.length === 0) {
      return (
        <span className={'flex flex-row gap-1 items-center'}>
          <SvgOK className={'text-complementary-success shrink-0'} />
          <COText label={tr('JobDetail.bulkDistribution.allOk', 'Vše v pořádku')} />
        </span>
      );
    }
    return (
      <ul className={'flex flex-col gap-1 pl-4 list-disc text-sm text-content-primary'}>
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    );
  };

  const renderActionsColumn = (row: ExpeditionBulkDistributionJobItemOutDTO) => {
    if (mode !== 'DRAFT' || !editFlag.granted) return null;
    return (
      <div className={'flex flex-col gap-2 items-end'}>
        <RingilButton
          mode={'outlined-rectangle'}
          label={tr('JobDetail.bulkDistribution.openExpedition', 'Otevřít přepravu')}
          dataTestId={`bulk-distribution-row-${row.sortOrder}-open`}
          onClick={() => nav.navigate(nav.urlFunctions.createExpeditionDetailV2(row.expeditionId))}
        />
        <RingilButton
          mode={'outlined-rectangle'}
          icon={<SvgDelete />}
          label={tr('JobDetail.bulkDistribution.remove', 'Odebrat')}
          dataTestId={`bulk-distribution-row-${row.sortOrder}-remove`}
          onClick={() => deleteRow(row.id)}
          {...busy(`delete-${row.id}`)}
        />
      </div>
    );
  };

  const renderResultColumn = (row: ExpeditionBulkDistributionJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.DONE) {
      return (
        <div className={'flex flex-col gap-1 items-start'}>
          <COText
            label={
              row.directAssign
                ? tr('JobDetail.bulkDistribution.assigned', 'Dopravce přiřazen')
                : tr('JobDetail.bulkDistribution.tendered', 'Poptáno ({{count}} dopravců)', { count: row.carrierCount ?? detail?.carrierCount ?? 0 })
            }
          />
          {detail?.createdByName != null && <span className={'text-sm text-content-tertiary'}>{detail.createdByName}</span>}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.ERROR) {
      const errorText = formatJobItemError(row.error, row.errorMessage, tr);
      return (
        <div className={'flex flex-col gap-1'}>
          <COText label={tr('JobDetail.bulkDistribution.notCreated', 'Nepoptáno')} />
          {isNotNullOrUndefined(errorText) && <span className={'text-content-tertiary text-sm'}>{errorText}</span>}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.RUNNING) {
      return <COText label={tr('JobDetail.item.inProgress', 'In progress')} />;
    }
    if (row.itemStatus === ItemStatusEnum.SKIPPED) {
      return <span className={'text-content-tertiary'}>{tr('JobDetail.item.skipped', 'Skipped')}</span>;
    }
    return <COText label={tr('JobDetail.item.waiting', 'Waiting')} />;
  };

  // The laden-factor column only earns its place when the request carries one.
  const withLadenFactor = detail?.ladenFactor != null;
  const draftColumns: ColumnConfig<ExpeditionBulkDistributionJobItemOutDTO>[] = [
    { id: 'expedition', header: 'JobDetail.bulkDistribution.table.expedition', headerDefault: 'Přeprava', width: withLadenFactor ? '22%' : '28%', order: 1, render: renderExpeditionColumn },
    { id: 'route', header: 'JobDetail.bulkDistribution.table.route', headerDefault: 'Trasa', width: withLadenFactor ? '22%' : '28%', order: 2, render: renderRouteColumn },
    ...(withLadenFactor
      ? [{ id: 'ladenFactor', header: 'JobDetail.bulkDistribution.table.ladenFactor', headerDefault: 'Ložný faktor', width: '16%', order: 3, render: renderLadenFactorColumn }]
      : []),
    { id: 'problems', header: 'JobDetail.bulkDistribution.table.problems', headerDefault: 'Problémy', width: withLadenFactor ? '26%' : '30%', order: 4, render: renderProblemsColumn },
    { id: 'actions', header: 'JobDetail.bulkDistribution.table.whatToDo', headerDefault: 'Co dělat', width: '14%', order: 5, render: renderActionsColumn },
  ];

  const resultColumns: ColumnConfig<ExpeditionBulkDistributionJobItemOutDTO>[] = [
    { id: 'expedition', header: 'JobDetail.bulkDistribution.table.expedition', headerDefault: 'Přeprava', width: '30%', order: 1, render: renderExpeditionColumn },
    { id: 'route', header: 'JobDetail.bulkDistribution.table.route', headerDefault: 'Trasa', width: '35%', order: 2, render: renderRouteColumn },
    { id: 'result', header: 'JobDetail.bulkDistribution.table.result', headerDefault: 'Výsledek', width: '35%', order: 3, render: renderResultColumn },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('JobDetail.bulkDistribution.filterState', 'Status'),
      label: tr('JobDetail.bulkDistribution.filterState', 'Status'),
      value: ['stateFilter'],
      options: Object.values(StateFilterEnum).map(state => ({ label: stateLabel(state), value: state, active: true })),
      dataTestId: 'bulk-distribution-job-filter-state',
    },
  ];

  if (detail == null) {
    return <SvgLoader className={'animate-spin'} />;
  }

  const { processedCount: processed, failedCount: failed, totalCount: total } = detail.job;
  const applicationId = detail.job.applicationId;
  const progressPct = total != null && total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;
  const canRetry = status === JobStatusEnum.ERROR || (status === JobStatusEnum.DONE && failed > 0);
  const isDone = status === JobStatusEnum.DONE;
  const okCount = Math.max((total ?? 0) - failed, 0);
  const canExecute = detail.itemsWithIssues === 0 && detail.itemsTotal > 0;
  const stepperOutcome = !isTerminal ? undefined : status === JobStatusEnum.ERROR ? 'error' : failed > 0 ? 'warning' : 'ok';
  const title = isDraftPhase
    ? `${tr('JobDetail.bulkDistribution.draftTitle', 'Poptat dopravce')} ${applicationId ?? ''}`
    : isDone
    ? `${tr('JobDetail.bulkDistribution.resultTitle', 'Výsledek poptávky')} ${applicationId ?? ''}`
    : `${tr('JobDetail.task', 'Úkol')} ${applicationId ?? ''}`;

  const deadline = detail.utcEnd != null ? dayjsFromApiUtcInTz(detail.utcEnd, detail.timezoneId ?? 'Europe/Prague') : null;
  const summaryParts = [
    kindLabel(detail.distributionKind),
    detail.price?.price != null ? `${detail.price.price} ${detail.price.currency ?? ''}`.trim() : null,
    detail.distributionKind !== DistributionKindEnum.ASSIGN ? tr('JobDetail.bulkDistribution.carriers', '{{count}} dopravců', { count: detail.carrierCount }) : null,
    detail.ladenFactor != null ? tr('JobDetail.bulkDistribution.ladenFactorSummary', 'Ložný faktor {{value}}', { value: detail.ladenFactor }) : null,
    deadline != null
      ? tr('JobDetail.bulkDistribution.deadline', 'Nabídky do {{date}} {{time}}', {
          date: formatDateFromDayjsWithLocale(deadline),
          time: stringHHmmFromDayjs(deadline),
        })
      : null,
  ].filter(isNotNullOrUndefined);

  const onActionError = (_e: unknown, showStandardError: () => void) => {
    setAction(null);
    showStandardError();
  };

  const runExecute = () => {
    setAction('execute');
    executeExpeditionBulkDistributionJob(jobId, () => reloadAll(() => setAction(null)), null, {
      successToast: false,
      // A 409 means another viewer changed the draft (or a row got its problem back): show it and refresh.
      onError: (e, showStandardError) => {
        onActionError(e, showStandardError);
        reloadAll();
      },
    });
  };

  const revalidate = () => {
    setAction('revalidate');
    revalidateExpeditionBulkDistributionJob(jobId, () => reloadAll(() => setAction(null)), null, { successToast: false, onError: onActionError });
  };

  const deleteRow = (rowId: string) => {
    setAction(`delete-${rowId}`);
    deleteExpeditionBulkDistributionItem(jobId, rowId, () => reloadAll(), null, { successToast: false, onError: onActionError });
  };

  const deleteDraft = () => {
    setAction('archive');
    deleteExpeditionBulkDistributionJob(jobId, () => nav.navigate(leaveDraftUrl()), null, { successToast: false, onError: onActionError });
  };

  const retry = () => {
    setAction('retry');
    retryJob(jobId, () => reloadAll(() => setAction(null)), null, { successToast: false, onError: onActionError });
  };

  const cancel = () => {
    setAction('cancel');
    cancelAsyncJob(jobId, () => load(() => setAction(null)), null, { onError: onActionError });
  };

  const archive = () => {
    setAction('archive');
    archiveJob(jobId, () => nav.navigate(nav.urlFunctions.createJobList()), null, { successToast: false, onError: onActionError });
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          { label: tr('JobList.title', 'Rozpracované úkoly'), url: nav.createNavigationLink(nav.urlFunctions.createJobList()) },
          { label: title, disabled: true },
        ]}
      />

      <DuplicationStepper activeStep={isDraftPhase ? 2 : 3} outcome={stepperOutcome} />

      <JobDetailHeader
        title={title}
        jobTypeLabel={tr(`JobList.typeBadge.${JobTypeEnum.EXPEDITION_BULK_DISTRIBUTION}`, 'Poptávka')}
        jobCategory={detail.jobCategory}
        createdByName={detail.createdByName}
        createdAt={detail.job.createdAt}
        buttons={
          mode === 'DRAFT' && editFlag.granted ? (
            <RingilButton
              mode={'outlined-rectangle'}
              icon={<SvgReset />}
              label={tr('JobDetail.bulkDistribution.refresh', 'Znovu zkontrolovat')}
              dataTestId={'bulk-distribution-revalidate'}
              onClick={revalidate}
              {...busy('revalidate')}
            />
          ) : undefined
        }
        onCancel={isRunning ? cancel : undefined}
        onArchive={mode === 'DRAFT' && editFlag.granted ? () => setDeleteConfirmVisible(true) : isTerminal && jobsRwFlag.granted ? archive : undefined}
        busyAction={action}
      />

      {/* What was tendered — the same for every row, so it lives above the table rather than in it. */}
      <div className={'flex flex-row flex-wrap items-center gap-2 text-content-secondary'} data-testid={'bulk-distribution-job-summary'}>
        {summaryParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className={'text-content-quaternary'}>·</span>}
            <span>{part}</span>
          </React.Fragment>
        ))}
      </div>

      {mode === 'DRAFT' && detail.itemsWithIssues > 0 && (
        <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('JobDetail.bulkDistribution.issuesStrip', 'V {{total}} přepravách máte {{issues}} problémů nebo chyb. Odeberte je, nebo je opravte a znovu zkontrolujte.', {
            total: detail.itemsTotal,
            issues: detail.itemsWithIssues,
          })}
        </COInlineMessage>
      )}

      {isRunning && (
        <div className={'flex flex-col'}>
          <COBanner
            variant={COBannerVariant.Warning}
            className={'rounded-t'}
            label={
              mode === 'EXECUTING' ? tr('JobDetail.bulkDistribution.executing', 'Vytvářím poptávky') : tr('JobDetail.bulkDistribution.validating', 'Kontroluji přepravy')
            }
          />
          <div className={'flex flex-col items-center gap-4 rounded-b bg-white py-12'}>
            <SvgLoader className={'h-[29px] w-[29px] animate-spin [&_path]:stroke-content-brand'} />
            <span className={'text-content-secondary'}>{tr('JobList.processedPct', '{{pct}} % zpracováno', { pct: progressPct })}</span>
          </div>
        </div>
      )}

      {isDone && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-6'}>
          <div className={'flex flex-row items-center gap-3'}>
            <SvgRobot width={32} height={32} />
            {failed > 0 ? (
              <>
                <SvgWarning className={'text-complementary-warning shrink-0'} />
                <span>
                  {tr('JobDetail.bulkDistribution.robotPartial', '{{failed}} přeprav se nepodařilo poptat, dalších {{ok}} bylo úspěšně poptáno.', {
                    failed,
                    ok: okCount,
                  })}
                </span>
              </>
            ) : (
              <>
                <SvgOK className={'text-complementary-success shrink-0'} />
                <span>{tr('JobDetail.bulkDistribution.robotAllOk', 'Všech {{count}} přeprav bylo úspěšně poptáno.', { count: total })}</span>
              </>
            )}
          </div>
          <RingilButton
            mode={'filled'}
            label={tr('JobDetail.bulkDistribution.showExpeditions', 'Zobrazit přepravy')}
            dataTestId={'bulk-distribution-job-show-expeditions'}
            onClick={() => nav.navigate(nav.urlFunctions.createCustomerExpeditionList())}
          />
        </div>
      )}

      {status === JobStatusEnum.ERROR && (
        <COInlineMessage variant={'error'} contentClassName={'flex-1 text-center'}>
          <div>{tr('JobDetail.bulkDistribution.failed', 'Poptávky se nepodařilo vytvořit.')}</div>
          {detail.job.errorMessage != null && <div>{detail.job.errorMessage}</div>}
        </COInlineMessage>
      )}
      {status === JobStatusEnum.CANCELLED && (
        <COInlineMessage variant={'info'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('JobDetail.bulkDistribution.cancelled', 'Úkol byl zrušen. Poptávky vytvořené před zrušením zůstávají.')}
        </COInlineMessage>
      )}

      {canRetry && jobsRwFlag.granted && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
          <RingilButton mode={'filled'} label={tr('JobList.retry', 'Zkusit znovu')} dataTestId={'bulk-distribution-job-retry'} onClick={retry} {...busy('retry')} />
        </div>
      )}

      <RingilDataTable
        ref={tableRef}
        columnConfig={isDraftPhase ? draftColumns : resultColumns}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'expedition_bulk_distribution_job_items'}
        // The issues filter is pre-applied when the check finds problems, so landing on it with nothing to show
        // means the draft is clean rather than that the user filtered badly.
        emptyMessage={f =>
          f?.stateFilter === StateFilterEnum.WITH_ERROR ? (
            <span>
              <strong>{tr('JobDetail.bulkDistribution.noIssuesCount', 'V {{count}} přepravách', { count: detail.itemsTotal })}</strong>{' '}
              {tr('JobDetail.bulkDistribution.noIssuesText', 'nemáte žádný problém, který by bránil poptání.')}{' '}
              <span className={clickableTextClass} onClick={() => tableRef.current?.applyFilterValue('stateFilter', null)}>
                {tr('JobDetail.bulkDistribution.showAllItems', 'Zobrazit přepravy')}
              </span>
            </span>
          ) : undefined
        }
      />

      {mode === 'DRAFT' && editFlag.granted && (
        <div className={'flex flex-col gap-2'}>
          <div className={'flex flex-row gap-2 items-center'}>
            <RingilButton
              mode={'filled-rectangle'}
              size={'big'}
              label={tr('JobDetail.bulkDistribution.checkedExecute', 'Zkontrolováno, poptat dopravce')}
              dataTestId={'bulk-distribution-execute'}
              onClick={runExecute}
              loading={action === 'execute'}
              disabled={!canExecute || (action != null && action !== 'execute')}
            />
            <RingilButton
              mode={'outlined-rectangle'}
              size={'big'}
              label={tr('JobDetail.bulkDistribution.saveForLater', 'Uložit na později')}
              dataTestId={'bulk-distribution-save-for-later'}
              onClick={() => nav.navigate(leaveDraftUrl())}
            />
          </div>
          {!canExecute && (
            <span className={'text-sm text-content-tertiary'} data-testid={'bulk-distribution-execute-blocked'}>
              {detail.itemsTotal === 0
                ? tr('JobDetail.bulkDistribution.executeNoRows', 'V seznamu nezůstala žádná přeprava.')
                : tr('JobDetail.bulkDistribution.executeBlocked', 'Nejdřív odeberte přepravy s problémy, nebo je opravte a znovu zkontrolujte.')}
            </span>
          )}
        </div>
      )}

      {isTerminal && <JobExpiryNote expiresAt={detail.expiresAt} />}

      <ConfirmDialog
        visible={deleteConfirmVisible}
        dialogTitle={tr('JobDetail.bulkDistribution.deleteTitle', 'Smazat seznam')}
        dialogMessage={tr('JobDetail.bulkDistribution.deleteConfirm', 'Opravdu smazat celý seznam? Žádná poptávka zatím nevznikla.')}
        onAcceptButtonText={tr('JobDetail.bulkDistribution.deleteAccept', 'Smazat')}
        onCancelButtonText={tr('JobDetail.bulkDistribution.cancel', 'Zrušit')}
        onConfirm={() => {
          setDeleteConfirmVisible(false);
          deleteDraft();
        }}
        onCancel={() => setDeleteConfirmVisible(false)}
        dataTestId={'bulk-distribution-delete-confirm'}
      />
    </div>
  );
};

export default ExpeditionBulkDistributionJobDetail;
